import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ApiResponse } from '@src/app/core/models/api-response.model';
import {
  DropdownItem,
  DropDownList,
} from '@src/app/core/models/drop-down-list.model';
import { SystemParameter } from '@src/app/core/models/system-parameter.model';
import { ApiWebSubmissionService } from '@src/app/core/services/api-web-submission.service';
import { TranslatableDropdownOption } from 'dist/sigv-library/model/src/lib/translatable-dropdown-option.model';
import {
  BehaviorSubject,
  combineLatest,
  EMPTY,
  iif,
  Observable,
  of,
  Subject,
} from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DropdownOptionsService, ToastService } from 'sigv-library';
import { ProductInfo } from '../../web-submission/models/product-info.model';

@Injectable()
export class PageCacheService {
  /**
   * @description 是否啟用暫存
   */
  enableCache = false;
  /**
   * @description 是否執行轉換幣別金額
   */
  doTransferCurrencyAmount = new BehaviorSubject<boolean>(false);
  /**
   * @description 產品參數 Subject
   */
  productInfoSubject: Subject<ProductInfo> = new Subject<ProductInfo>();
  /**
   * @description 下拉選單查詢後暫存
   */
  private cacheParams: { [key: string]: SystemParameter[] };

  /**
   * @description 中租共用下拉暫存
   */
  private cacheDropdownList: { [key: string]: DropdownItem[] };

  /**
   * @description 中租共用下拉暫存
   */
  private cacheDropdownListNew: { [key: string]: TranslatableDropdownOption[] };

  constructor(
    private apiWebSubmissionService: ApiWebSubmissionService,
    private translateService: TranslateService,
    private toastService: ToastService,
    private dropdownOptionService: DropdownOptionsService
  ) {
    this.cacheParams = {};
    this.cacheDropdownList = {};
    this.cacheDropdownListNew = {};
  }

  /**
   * @description 依據指定的 parent code 取得(連動)系統參數設定資料, 避免重覆查詢會將查過的資料暫存(memory)
   * @param parentCode 取得系統參數設定的 parent code
   * @returns 指定 parent code 的系統參數設定資料
   */
  getSystemParameterByParent(
    parentCode: string
  ): Observable<SystemParameter[]> {
    return this.getSystemParameterSubject(
      parentCode,
      `/Common/GetSystemParameterByParent/${parentCode}`
    );
  }

  /**
   * @description 依據指定的 type code 取得系統參數設定資料, 避免重覆查詢會將查過的資料暫存(memory)
   * @param typeCode 取得系統參數設定的 type code
   * @returns 指定 type code 的系統參數設定資料
   */
  getSystemParameter(typeCode: string): Observable<SystemParameter[]> {
    return this.getSystemParameterSubject(
      typeCode,
      `/Common/GetSystemParameter/${typeCode}`
    );
  }

  /**
   * @description 取得中租指定類型下拉選單, 避免重覆查詢會將查過的資料暫存(memory)
   * @param paramType 取得中租共用下拉選單類型代碼
   * @param filterByIsEnable 是否篩選出 isEnable 為 true 的項目(檢視模式時不需篩選)
   * @returns 指定類型下拉選單資料
   */
  getDropdownList(
    paramType: string,
    code: string = '',
    filterByIsEnable = true
  ): Observable<DropdownItem[]> {
    return this.getDropdownlistSubject(paramType, code, filterByIsEnable);
  }

  /**
   * @description 取得中租指定類型下拉選單, 避免重覆查詢會將查過的資料暫存(memory)
   * @param paramType 取得中租共用下拉選單類型代碼
   * @param filterByIsEnable 是否篩選出 isEnable 為 true 的項目(檢視模式時不需篩選)
   * @returns 指定類型下拉選單資料
   */
  getDropdownListNew(
    paramType: string,
    code: string = '',
    filterByIsEnable = true
  ): Observable<TranslatableDropdownOption[]> {
    return this.getDropdownlistSubjectNew(paramType, code, filterByIsEnable);
  }

  private getSystemParameterSubject(
    code: string,
    apiPathWithCode: string
  ): Observable<SystemParameter[]> {
    if (code && apiPathWithCode) {
      const subject = new BehaviorSubject<SystemParameter[]>([]);
      if (this.enableCache && this.cacheParams[code]) {
        subject.next(this.cacheParams[code]);
      } else {
        this.apiWebSubmissionService
          .doGet<ApiResponse<SystemParameter[]>>(apiPathWithCode)
          .subscribe((response: ApiResponse<SystemParameter[]>) => {
            if (response && response.rtnCode === 0) {
              if (response.data && response.data.length > 0) {
                this.cacheParams[code] = response.data!;
                subject.next(response.data!);
              } else {
                this.toastService.error(
                  this.translateService.instant(
                    'Common.Message.LoadSystemParameterEmpty',
                    { code }
                  )
                );
                subject.next([]);
              }
            } else {
              this.toastService.error(
                this.translateService.instant(
                  'Common.Message.LoadSystemParameterError',
                  { code }
                )
              );
              subject.next([]);
            }
          });
      }
      return subject.asObservable();
    } else {
      return of([]);
    }
  }

  private getDropdownlistSubject(
    paramType: string,
    code: string = '',
    filterByIsEnable = true
  ): Observable<DropdownItem[]> {
    if (paramType && code === '') {
      return iif(
        () => this.enableCache && !!this.cacheDropdownList[paramType],
        of(this.cacheDropdownList[paramType]),
        this.apiWebSubmissionService
          .doPost<ApiResponse<DropDownList[]>>('/Common/GetDropdownLists', {
            paramTypes: [
              {
                code: '',
                paramType,
              },
            ],
          })
          .pipe(
            map((res) => {
              if (res && res.rtnCode === 0 && res.data) {
                if (res.data.length === 0) {
                  this.toastService.error(
                    this.translateService.instant(
                      'Common.Message.LoadDropdownListEmpty',
                      { paramType }
                    )
                  );
                  return [];
                } else {
                  this.cacheDropdownList[paramType] = res.data
                    .reduce((pre, curr) => {
                      const list = filterByIsEnable
                        ? curr.sysParams.filter((item) => !!item.isEnable)
                        : curr.sysParams;
                      return [...pre, ...list];
                    }, [] as DropdownItem[])
                    .sort((a, b) => a.order - b.order);
                  return this.cacheDropdownList[paramType];
                }
              } else {
                this.toastService.error(
                  this.translateService.instant(
                    'Common.Message.LoadDropdownListError',
                    { paramType }
                  )
                );
                return [];
              }
            }),
            catchError((error) => {
              this.toastService.error(
                this.translateService.instant(
                  'Common.Message.LoadDropdownListError',
                  { paramType }
                )
              );
              return EMPTY;
            })
          )
      );
    } else if (paramType && code !== '') {
      return this.apiWebSubmissionService
        .doPost<ApiResponse<DropDownList[]>>('/Common/GetDropdownLists', {
          paramTypes: [
            {
              code,
              paramType,
            },
          ],
        })
        .pipe(
          map((res) => {
            if (res && res.rtnCode === 0 && res.data) {
              this.cacheDropdownList[paramType] = res.data
                .reduce((pre, curr) => {
                  const list = filterByIsEnable
                    ? curr.sysParams.filter((item) => !!item.isEnable)
                    : curr.sysParams;
                  return [...pre, ...list];
                }, [] as DropdownItem[])
                .sort((a, b) => a.order - b.order);
              return this.cacheDropdownList[paramType];
            } else {
              return [];
            }
          })
        );
    } else {
      return of([]);
    }
  }

  private getDropdownlistSubjectNew(
    paramType: string,
    code: string = '',
    filterByIsEnable = true
  ): Observable<TranslatableDropdownOption[]> {
    if (paramType && code === '') {
      return iif(
        () => this.enableCache && !!this.cacheDropdownListNew[paramType],
        of(this.cacheDropdownListNew[paramType]),
        this.dropdownOptionService.getCommonDropdownOptions(paramType).pipe(
          map((res) => {
            this.cacheDropdownListNew[paramType] = filterByIsEnable
              ? res.filter((item) => !item.disabled)
              : res;

            return this.cacheDropdownListNew[paramType];
          }),
          catchError((error) => {
            this.toastService.error(
              this.translateService.instant(
                'Common.Message.LoadDropdownListError',
                { paramType }
              )
            );
            return EMPTY;
          })
        )
      );
    } else if (paramType && code !== '') {
      return this.dropdownOptionService
        .getCommonDropdownOptions(paramType, code)
        .pipe(
          map((res) => {
            this.cacheDropdownListNew[paramType] = filterByIsEnable
              ? res.filter((item) => !item.disabled)
              : res;

            return this.cacheDropdownListNew[paramType];
          })
        );
    } else {
      return of([]);
    }
  }

  /**
   * @description 取得審查暫存 Model 下拉選單
   * @param parentCodeList 取得審查暫存 Model 下拉選單 Brand 列表
   * @returns Model 下拉選單資料
   */
  getCustomModelList(
    companyId: string,
    parentCodeList: string[]
  ): Observable<{ [key: string]: DropdownItem[] }> {
    if (companyId && parentCodeList.length) {
      const modelAPIlist = [...new Set(parentCodeList)].map((parentCode) => {
        const httpParam = new HttpParams().append('parentCode', parentCode);

        return this.apiWebSubmissionService.doGet<ApiResponse<DropdownItem[]>>(
          '/Common/GetModel',
          httpParam,
          {
            'X-CompanyId': companyId.toString(),
          }
        );
      });

      return combineLatest(modelAPIlist).pipe(
        map((responseList) => {
          return responseList.reduce((prev, { data, rtnCode }) => {
            if (data?.length && rtnCode === 0) {
              const key = data[0].parentCode;
              const list = data.map((item) => ({
                ...item,
                // 將 value 取代成 code
                value: item.code,
              }));

              return { ...prev, [key]: list };
            }
            return prev;
          }, {} as { [key: string]: DropdownItem[] });
        })
      );
    }

    return of({});
  }
}
